import {createContext, Dispatch, ReactNode, SetStateAction, useCallback, useContext, useEffect, useState} from "react";
import {usePaginate} from "../PaginateProvider";
import {useLocation} from "react-router-dom";
import {ProposalSaleOriginModel} from "../../models/ProposalsTrack/ProposalSaleOriginModel";
import {ProposalsSaleOriginService} from "../../services/Proposals/ProposalsSaleOriginService";

interface ContextProps {
    proposalsSaleOrigin: ProposalSaleOriginModel[],
    setProposalsSaleOrigin: Dispatch<SetStateAction<ProposalSaleOriginModel[]>>,
    isLoading: boolean,
    error: string,
    params: any,
    setParams: Dispatch<SetStateAction<any>>,
    handleList: () => Promise<any>,
    handleGet: () => Promise<any>,
    handleSave: (payload: any) => Promise<any>,
    handleInative: (id: number | null) => Promise<any>,
}

export const ProposalsSaleOriginContext = createContext<ContextProps>({} as ContextProps);

interface ProposalsSaleOriginProviderProps {
    children: ReactNode
}

export const ProposalsSaleOriginProvider = ({children}: ProposalsSaleOriginProviderProps) => {
    const [proposalsSaleOrigin, setProposalsSaleOrigin] = useState<ProposalSaleOriginModel[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>('');
    const [params, setParams] = useState<any>({page: 1});

    const {setPagesPaginate} = usePaginate();
    const location = useLocation();
    const service = new ProposalsSaleOriginService();

    const handleList = useCallback(async () => {
        if (!location.pathname.includes('/proposal-track/sale-origin/list')) {
            return false;
        }

        try {
            setIsLoading(true);
            const [_Response, _Error] = await service.list(params);

            if (!!_Error) {
                setIsLoading(false);
                return setError(_Response || _Error);
            }

            setProposalsSaleOrigin(_Response.data);
            setPagesPaginate(_Response.pages);

            return setIsLoading(false);
        } catch (e) {
            console.warn(e);
            return setIsLoading(false);
        }
    }, [location.pathname, params]);

    const handleGet = async () => {
        setIsLoading(true);

        try {
            const [_Response, _Error] = await service.get();

            if (!!_Error) {
                setIsLoading(false);
                return setError(_Response || _Error);
            }

            setProposalsSaleOrigin(_Response.data);

            return setIsLoading(false);
        } catch (e) {
            console.warn(e);
            return setIsLoading(false);
        }
    }


    const handleSave = async (payload: any) => {
        setIsLoading(true);

        try {
            const [_Response, _Error] = await service.save(payload);

            if (!!_Error) {
                setIsLoading(false);
                setError(_Response || _Error);
                return _Error;
            }

            setIsLoading(false);
            return "";
        } catch (e) {
            console.warn(e);
            setIsLoading(false);
            return e;
        }
    }

    const handleInative = async (id: number | null) => {
        setIsLoading(true);

        try {
            const [_Response, _Error] = await service.delete(id);

            if (!!_Error) {
                setIsLoading(false);
                setError(_Response || _Error);
                return _Error;
            }


            setIsLoading(false);
            return "";
        } catch (e) {
            console.warn(e);
            setIsLoading(false);
            return e;
        }
    }

    useEffect(() => {
        handleList().then();
    }, [handleList]);

    return (
        <ProposalsSaleOriginContext.Provider value={{
            proposalsSaleOrigin,
            setProposalsSaleOrigin,
            isLoading,
            error,
            params,
            setParams,
            handleList,
            handleGet,
            handleSave,
            handleInative,
        }}>
            {children}
        </ProposalsSaleOriginContext.Provider>
    )
}

export const useProposalsSaleOrigin = () => useContext(ProposalsSaleOriginContext);
